import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, ShoppingCart, X } from 'lucide-react';
import Footer from '../../components/layout/Footer';
import Navbar from '../../components/layout/Navbar';
import { supabase } from '../../lib/supabaseClient';
import Seo from '../../components/common/Seo';

const GHANA_LOCATIONS = {
  'Greater Accra': ['Accra', 'Tema', 'Madina', 'Kasoa', 'Adenta'],
  Ashanti: ['Kumasi', 'Obuasi', 'Ejisu', 'Mampong'],
  'Western Region': ['Takoradi', 'Sekondi', 'Tarkwa'],
  'Central Region': ['Cape Coast', 'Winneba', 'Kasoa'],
  'Eastern Region': ['Koforidua', 'Akosombo', 'Nsawam'],
  'Northern Region': ['Tamale', 'Yendi', 'Savelugu'],
  'Upper East': ['Bolgatanga', 'Navrongo', 'Bawku'],
  'Upper West': ['Wa', 'Lawra', 'Tumu'],
  Volta: ['Ho', 'Keta', 'Hohoe'],
  Oti: ['Dambai', 'Jasikan', 'Nkwanta'],
  Bono: ['Sunyani', 'Berekum', 'Dormaa Ahenkro'],
  Ahafo: ['Goaso', 'Hwidiem', 'Kenyasi'],
  BonoEast: ['Techiman', 'Kintampo', 'Atebubu'],
  Savannah: ['Damongo', 'Bole', 'Salaga'],
  NorthEast: ['Nalerigu', 'Walewale', 'Gambaga'],
  'Western North': ['Sefwi Wiawso', 'Bibiani', 'Aowin']
};

export default function Store() {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [checkoutEmail, setCheckoutEmail] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartItems, setCartItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem('gmw_cart')) || []; } catch { return []; }
  });
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutMessage, setCheckoutMessage] = useState('');
  const [checkoutError, setCheckoutError] = useState('');
  const [variantsByProduct, setVariantsByProduct] = useState({});
  const [variantSelection, setVariantSelection] = useState({});
  const [storeLoading, setStoreLoading] = useState(true);
  const [cartNotice, setCartNotice] = useState('');
  const [shipping, setShipping] = useState({
    name: '',
    phone: '',
    address: '',
    city: '',
    region: '',
    notes: ''
  });

  const sectionFade = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
  };

  const stagger = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.08, delayChildren: 0.05 }
    }
  };

  const [storeItems, setStoreItems] = useState([]);
  const [storeContent, setStoreContent] = useState({
    viewImage: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=1600&auto=format&fit=crop',
    collectionImage: 'https://images.unsplash.com/photo-1521572267360-ee0c2909d518?w=1600&auto=format&fit=crop',
    newsletterImage: 'https://images.unsplash.com/photo-1521572267360-ee0c2909d518?w=1600&auto=format&fit=crop',
    latestImage: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=1800&auto=format&fit=crop'
  });
  const [highlightProductId, setHighlightProductId] = useState('');
  const money = (cents, currency = 'GHS') => `${currency} ${Math.round((Number(cents) || 0) / 100)}`;
  const emailIsValid = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

  useEffect(() => {
    const load = async () => {
      setStoreLoading(true);
      const [{ data: homeContent }, { data, error }] = await Promise.all([
        supabase
          .from('homepage_content')
          .select('store_hero_view_image_url,store_hero_collection_image_url,store_newsletter_image_url,store_latest_image_url')
          .order('updated_at', { ascending: false })
          .limit(1),
        supabase.from('store_products').select('*').eq('is_active', true).order('created_at', { ascending: false })
      ]);
      if (homeContent?.[0]) {
        setStoreContent((prev) => ({
          ...prev,
          viewImage: homeContent[0].store_hero_view_image_url || prev.viewImage,
          collectionImage: homeContent[0].store_hero_collection_image_url || prev.collectionImage,
          newsletterImage: homeContent[0].store_newsletter_image_url || prev.newsletterImage,
          latestImage: homeContent[0].store_latest_image_url || prev.latestImage
        }));
      }
      if (!error && data?.length) {
        const mapped = data.map((item) => ({
          id: item.id,
          name: item.name,
          price: `${item.currency} ${Math.round(item.price_cents / 100)}`,
          tag: item.tag,
          image: item.image_url,
          price_cents: item.price_cents,
          currency: item.currency
        }));
        setStoreItems(mapped);
      } else {
        setStoreItems([]);
      }
      setStoreLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const productId = params.get('product');
    if (!productId) return;
    const el = document.getElementById(`product-card-${productId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightProductId(productId);
    const clear = setTimeout(() => setHighlightProductId(''), 2500);
    return () => clearTimeout(clear);
  }, [storeItems]);

  const gridProducts = storeItems.slice(0, 3);
  const productGrid = storeItems.slice(3, 6).length ? storeItems.slice(3, 6) : storeItems.slice(0, 3);
  const storeSchemas = [];

  if (storeItems.length) {
    const productSchemas = storeItems.map((item, idx) => {
      const variants = variantsByProduct[item.id] || [];
      const firstVariant = variants[0];
      const availability = variants.length
        ? variants.some((v) => Number(v.stock ?? 0) > 0)
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock'
        : 'https://schema.org/InStock';
      const numericPrice = Number(firstVariant?.price_cents ?? item.price_cents ?? 0) / 100;
      const image = String(item.image || '').startsWith('http') ? item.image : undefined;

      return {
        '@context': 'https://schema.org',
        '@type': 'Product',
        position: idx + 1,
        name: item.name,
        image,
        brand: {
          '@type': 'Brand',
          name: 'GameMeetsWorld'
        },
        offers: {
          '@type': 'Offer',
          priceCurrency: item.currency || 'GHS',
          price: String(numericPrice),
          availability,
          url: `https://gamemeetsworld.com/store?product=${item.id}`
        }
      };
    });

    const itemList = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: storeItems.map((item, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: `https://gamemeetsworld.com/store?product=${item.id}`
      }))
    };

    storeSchemas.push(itemList, ...productSchemas);
  }

  useEffect(() => {
    const loadVariants = async () => {
      if (!storeItems.length) return;
      const ids = storeItems.map((i) => i.id);
      const { data, error } = await supabase.from('store_variants').select('*').in('product_id', ids);
      if (error || !data) return;
      const grouped = data.reduce((acc, v) => {
        acc[v.product_id] = acc[v.product_id] || [];
        acc[v.product_id].push(v);
        return acc;
      }, {});
      setVariantsByProduct(grouped);
      const defaults = {};
      Object.keys(grouped).forEach((pid) => {
        if (grouped[pid]?.length) defaults[pid] = grouped[pid][0].id;
      });
      setVariantSelection((prev) => ({ ...defaults, ...prev }));
    };
    loadVariants();
  }, [storeItems]);

  useEffect(() => {
    if (!cartNotice) return;
    const timer = setTimeout(() => setCartNotice(''), 2200);
    return () => clearTimeout(timer);
  }, [cartNotice]);

  useEffect(() => {
    localStorage.setItem('gmw_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reference = params.get('reference');
    if (!reference) return;
      const verify = async () => {
        setCheckoutMessage('Verifying payment...');
        const res = await fetch(`/api/checkout/verify?reference=${reference}`);
        if (res.ok) {
          setCheckoutMessage('Payment verified. Thank you!');
          setCartItems([]);
          params.delete('reference');
          window.history.replaceState({}, '', `/order-confirmation?reference=${reference}`);
          window.location.assign(`/order-confirmation?reference=${reference}`);
        } else {
          setCheckoutMessage('Payment verification failed.');
        }
      };
    verify();
  }, []);

  const addToCart = (product) => {
    const variantId = variantSelection[product.id];
    const variants = variantsByProduct[product.id] || [];
    const variant = variants.find((v) => v.id === variantId);
    const maxStock = variant?.stock ?? null;
    const price_cents = variant?.price_cents ?? product.price_cents ?? 0;
    setCartItems((items) => {
      const existing = items.find((i) => i.id === product.id && i.variant_id === variantId);
      if (existing) {
        if (maxStock != null && existing.qty >= maxStock) {
          setCartNotice(`Only ${maxStock} left for ${product.name}${variant?.name ? ` (${variant.name})` : ''}.`);
          return items;
        }
        return items.map((i) => (i.id === product.id && i.variant_id === variantId ? { ...i, qty: i.qty + 1 } : i));
      }
      return [
        ...items,
        {
          id: product.id,
          name: product.name,
          price_cents,
          currency: product.currency || 'GHS',
          image: product.image,
          qty: 1,
          variant_id: variant?.id || null,
          variant_name: variant?.name || null,
          stock: variant?.stock ?? null
        }
      ];
    });
    setCartNotice(`${product.name}${variant?.name ? ` (${variant.name})` : ''} added to cart.`);
    setCartOpen(true);
  };

  const selectedVariantForProduct = (productId) => {
    const variants = variantsByProduct[productId] || [];
    return variants.find((v) => v.id === variantSelection[productId]) || null;
  };

  const updateQty = (id, variantId, delta) => {
    setCartItems((items) => items.map((i) => {
      if (!(i.id === id && i.variant_id === variantId)) return i;
      const nextQty = Math.max(1, i.qty + delta);
      if (i.stock != null && nextQty > i.stock) {
        setCartNotice(`Only ${i.stock} left for ${i.name}${i.variant_name ? ` (${i.variant_name})` : ''}.`);
        return i;
      }
      return { ...i, qty: nextQty };
    }));
  };

  const removeItem = (id, variantId) => {
    setCartItems((items) => items.filter((i) => !(i.id === id && i.variant_id === variantId)));
  };

  const cartCount = cartItems.reduce((sum, item) => sum + item.qty, 0);
  const subtotal = cartItems.reduce((sum, item) => sum + item.price_cents * item.qty, 0);
  const shippingFee = subtotal >= 50000 || subtotal === 0 ? 0 : 2500;
  const orderTotal = subtotal + shippingFee;
  const isCheckoutReady =
    cartItems.length > 0 &&
    emailIsValid(checkoutEmail) &&
    shipping.name.trim() &&
    shipping.phone.trim() &&
    shipping.address.trim() &&
    shipping.city.trim() &&
    shipping.region.trim();
  const regions = Object.keys(GHANA_LOCATIONS);
  const availableCities = GHANA_LOCATIONS[shipping.region] || [];

  const startCheckout = async () => {
    const normalizedEmail = checkoutEmail.trim();
    if (!emailIsValid(normalizedEmail)) {
      setCheckoutError('Enter a valid email to checkout.');
      return;
    }
    if (!shipping.name || !shipping.phone || !shipping.address || !shipping.city || !shipping.region) {
      setCheckoutError('Please fill out shipping details.');
      return;
    }
    if (!cartItems.length) return;
    setCheckoutLoading(true);
    setCheckoutError('');
    setCheckoutMessage('');
    const normalizedShipping = {
      name: shipping.name.trim(),
      phone: shipping.phone.trim(),
      address: shipping.address.trim(),
      city: shipping.city.trim(),
      region: shipping.region.trim(),
      notes: shipping.notes.trim()
    };
    const res = await fetch('/api/checkout/init', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: normalizedEmail,
        items: cartItems.map((i) => ({ id: i.id, quantity: i.qty, variant_id: i.variant_id })),
        shipping: normalizedShipping
      })
    });
    if (res.ok) {
      const data = await res.json();
      window.location.href = data.authorization_url;
    } else {
      const err = await res.json().catch(() => ({}));
      setCheckoutError(err.error || 'Checkout failed.');
    }
    setCheckoutLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#021206] text-white">
      <Seo
        title="Store"
        description="Shop official GameMeetsWorld merch and stream-inspired drops."
        path="/store"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Store', path: '/store' }
        ]}
        structuredData={storeSchemas}
      />
      <Navbar mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      <button
        onClick={() => setCartOpen(true)}
        className="fixed bottom-6 right-6 z-40 bg-[#A1FF4F] text-black w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
        aria-label="Open cart"
      >
        <ShoppingCart className="w-6 h-6" />
        {cartCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full text-[11px] font-bold bg-white text-black flex items-center justify-center">
            {cartCount}
          </span>
        )}
      </button>
      <div className="pt-28">
        {/* Promo Bar */}
        <div className="bg-white/5 text-white/80 text-xs uppercase tracking-[0.2em] text-center py-3 border-y border-white/10">
          10% discount for registered users &amp; free shipping over GHS 500
        </div>
      </div>

      {/* Hero Grid */}
      <motion.section
        className="max-w-7xl mx-auto px-6 lg:px-12 pt-12"
        variants={sectionFade}
        initial="hidden"
        animate="visible"
      >
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
          <motion.div
            className="relative h-[280px] md:h-[360px] rounded-[28px] overflow-hidden bg-[#0A0E11]"
            whileHover={{ scale: 1.01 }}
            transition={{ duration: 0.3 }}
          >
            <img
              src={storeContent.viewImage}
              alt="Latest drops"
              className="w-full h-full object-cover"
            />
            <div className="absolute left-8 top-8">
              <div className="w-16 h-16 rounded-full border border-white/70 flex items-center justify-center text-white text-xs uppercase tracking-widest">
                View
              </div>
            </div>
          </motion.div>

          <motion.div
            className="relative h-[280px] md:h-[360px] rounded-[28px] overflow-hidden flex items-end bg-white/10"
            whileHover={{ scale: 1.01 }}
            transition={{ duration: 0.3 }}
          >
            <img
              src={storeContent.collectionImage}
              alt="New collection"
              className="w-full h-full object-cover opacity-95"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <h2 className="absolute bottom-8 left-8 text-white text-4xl md:text-5xl font-black uppercase">
              New Collection
            </h2>
          </motion.div>
        </div>
      </motion.section>
      {storeLoading && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 mt-10">
          <p className="text-white/60">Loading store items...</p>
        </section>
      )}
      {!storeLoading && storeItems.length === 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 mt-10">
          <p className="text-white/60">No store items yet.</p>
        </section>
      )}

      {/* Info Cards */}
      <motion.section
        className="max-w-7xl mx-auto px-6 lg:px-12 mt-16"
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
      >
        <div className="grid md:grid-cols-3 gap-8 text-sm">
          {[
            { title: 'Payment Options', desc: 'Secure payments via Mobile Money, Visa, and Mastercard.' },
            { title: 'Sizing Info', desc: 'Available in S, M, L, XL, XXL, XXXL. Confirm sizing before ordering.' },
            { title: 'Delivery', desc: 'Delivery in 10 days after payment is made to confirm order.' }
          ].map((item) => (
            <motion.div
              key={item.title}
              className="flex gap-4"
              variants={sectionFade}
            >
              <div className="w-10 h-10 border border-white/30 flex items-center justify-center text-xs font-bold text-white/80">
                ■
              </div>
              <div>
                <p className="font-black uppercase tracking-widest text-xs text-white">{item.title}</p>
                <p className="text-white/70 mt-2 leading-relaxed">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Product Thumbs */}
      <motion.section
        className="max-w-7xl mx-auto px-6 lg:px-12 mt-16"
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
      >
        <div className="grid md:grid-cols-3 gap-6">
          {gridProducts.map((product) => (
            <motion.div
              key={product.id}
              id={`product-card-${product.id}`}
              className="rounded-[28px] overflow-hidden bg-[#0A0E11] border border-white/10"
              variants={sectionFade}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.3 }}
              animate={highlightProductId === product.id ? { boxShadow: '0 0 0 2px #A1FF4F' } : {}}
            >
              <img src={product.image} alt={product.name} className="w-full h-[260px] object-cover" />
              <div className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-bold">{product.name}</p>
                  <p className="text-white/60 text-sm">{product.price}</p>
                </div>
                <div className="flex items-center gap-2">
                  {variantsByProduct[product.id]?.length ? (
                    <select
                      value={variantSelection[product.id] || ''}
                      onChange={(e) => setVariantSelection((prev) => ({ ...prev, [product.id]: e.target.value }))}
                      className="bg-black/40 border border-white/10 px-2 py-1 rounded text-xs"
                    >
                      {variantsByProduct[product.id].map((v) => (
                        <option key={v.id} value={v.id}>{v.name}{v.stock != null ? ` (${v.stock} left)` : ''}</option>
                      ))}
                    </select>
                  ) : null}
                  <button
                    onClick={() => addToCart(product)}
                    disabled={selectedVariantForProduct(product.id)?.stock === 0}
                    className="px-3 py-2 bg-white/10 rounded hover:bg-[#A1FF4F] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                  Add
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="mt-10 overflow-hidden border-t border-b border-white/10 py-6">
          <div className="marquee-track text-4xl md:text-6xl font-black uppercase tracking-wide text-white/80">
            <span>GameMeetsWorld*</span>
            <span>GameMeetsWorld*</span>
            <span>GameMeetsWorld*</span>
            <span>GameMeetsWorld*</span>
            <span>GameMeetsWorld*</span>
            <span>GameMeetsWorld*</span>
            <span>GameMeetsWorld*</span>
            <span>GameMeetsWorld*</span>
          </div>
        </div>
      </motion.section>

      {/* Newsletter Promo */}
      <motion.section
        className="max-w-7xl mx-auto px-6 lg:px-12 mt-16"
        variants={sectionFade}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-120px' }}
      >
        <div className="grid lg:grid-cols-[1fr_1fr] gap-8 items-center">
          <div>
            <h3 className="text-3xl md:text-5xl font-black uppercase leading-tight">
              Receive 12% off your next order.
            </h3>
            <p className="text-white/70 mt-4">Subscribe to our newsletter for alerts on new drops.</p>
            <div className="mt-8 border-b border-white/40 flex items-center gap-4 pb-3">
              <input
                type="email"
                placeholder="Email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="bg-transparent w-full outline-none placeholder:text-white/40 text-white"
              />
              <ArrowUpRight className="w-4 h-4" />
            </div>
          </div>
          <motion.div
            className="rounded-[28px] overflow-hidden bg-[#0A0E11] border border-white/10"
            whileHover={{ scale: 1.01 }}
            transition={{ duration: 0.3 }}
          >
            <img
              src={storeContent.newsletterImage}
              alt="Newsletter"
              className="w-full h-[320px] object-cover"
            />
          </motion.div>
        </div>
      </motion.section>

      {/* Latest and Greatest */}
      <motion.section
        className="max-w-7xl mx-auto px-6 lg:px-12 mt-16"
        variants={sectionFade}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-120px' }}
      >
        <div className="relative rounded-[28px] overflow-hidden h-[360px] border border-white/10">
          <img
            src={storeContent.latestImage}
            alt="Latest and greatest"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/35" />
          <div className="absolute inset-0 flex items-center justify-center">
            <h3 className="text-white text-4xl md:text-6xl font-black uppercase">
              The Latest And Greatest
            </h3>
          </div>
        </div>
      </motion.section>

      {/* New Drops Grid */}
      <motion.section
        className="max-w-7xl mx-auto px-6 lg:px-12 mt-16"
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-120px' }}
      >
        <div className="grid md:grid-cols-3 gap-8">
          {productGrid.map((product) => (
            <motion.div
              key={product.id}
              id={`product-card-secondary-${product.id}`}
              className="relative"
              variants={sectionFade}
              animate={highlightProductId === product.id ? { boxShadow: '0 0 0 2px #A1FF4F' } : {}}
            >
              <span className="absolute -left-3 -top-3 bg-[#A1FF4F] text-black text-xs font-bold rounded-full w-12 h-12 flex items-center justify-center">
                {product.tag || 'NEW'}
              </span>
              <div className="rounded-[28px] overflow-hidden bg-[#0A0E11] border border-white/10">
                <img src={product.image} alt={product.name} className="w-full h-[300px] object-cover" />
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <p className="font-bold">{product.name}</p>
                  <p className="text-white/60 text-sm">{product.price}</p>
                </div>
                <div className="flex items-center gap-2">
                  {variantsByProduct[product.id]?.length ? (
                    <select
                      value={variantSelection[product.id] || ''}
                      onChange={(e) => setVariantSelection((prev) => ({ ...prev, [product.id]: e.target.value }))}
                      className="bg-black/40 border border-white/10 px-2 py-1 rounded text-xs"
                    >
                      {variantsByProduct[product.id].map((v) => (
                        <option key={v.id} value={v.id}>{v.name}{v.stock != null ? ` (${v.stock} left)` : ''}</option>
                      ))}
                    </select>
                  ) : null}
                  <button
                    onClick={() => addToCart(product)}
                    disabled={selectedVariantForProduct(product.id)?.stock === 0}
                    className="px-3 py-2 bg-white/10 rounded hover:bg-[#A1FF4F] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Add
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Cart Drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setCartOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-[#0A0E11] border-l border-white/10 p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold">Your Cart ({cartCount})</h3>
              <div className="flex items-center gap-2">
                {cartItems.length > 0 && (
                  <button
                    onClick={() => setCartItems([])}
                    className="px-3 py-1.5 text-xs bg-white/10 rounded hover:bg-white/20 transition-colors"
                  >
                    Clear
                  </button>
                )}
                <button onClick={() => setCartOpen(false)} className="p-2 bg-white/5 rounded">
                <X className="w-5 h-5" />
              </button>
              </div>
            </div>
            {cartNotice && <p className="mb-4 text-sm text-[#A1FF4F]">{cartNotice}</p>}

            {cartItems.length === 0 ? (
              <div>
                <p className="text-white/60">Your cart is empty.</p>
                <button onClick={() => setCartOpen(false)} className="mt-4 px-4 py-2 bg-white/10 rounded hover:bg-white/20 transition-colors">
                  Continue Shopping
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={`${item.id}:${item.variant_id || 'base'}`} className="flex items-center gap-4 bg-white/5 p-3 rounded">
                    <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded" />
                  <div className="flex-1">
                    <p className="font-semibold">{item.name}</p>
                    <p className="text-white/60 text-sm">{money(item.price_cents, item.currency)}</p>
                    {item.variant_name && <p className="text-white/40 text-xs">Variant: {item.variant_name}</p>}
                    <div className="flex items-center gap-2 mt-2">
                        <button onClick={() => updateQty(item.id, item.variant_id, -1)} className="px-2 py-1 bg-white/10 rounded">-</button>
                        <span>{item.qty}</span>
                        <button onClick={() => updateQty(item.id, item.variant_id, 1)} className="px-2 py-1 bg-white/10 rounded">+</button>
                      </div>
                    </div>
                    <button onClick={() => removeItem(item.id, item.variant_id)} className="text-red-400 text-sm">Remove</button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 border-t border-white/10 pt-4">
              <div className="flex items-center justify-between text-sm text-white/70">
                <p>Subtotal</p>
                <p>{money(subtotal)}</p>
              </div>
              <div className="flex items-center justify-between text-sm text-white/70 mt-1">
                <p>Shipping</p>
                <p>{shippingFee === 0 ? 'Free' : money(shippingFee)}</p>
              </div>
              <div className="flex items-center justify-between mt-3">
                <p className="text-white/90 font-semibold">Total</p>
                <p className="text-2xl font-bold">{money(orderTotal)}</p>
              </div>
              {subtotal > 0 && subtotal < 50000 && (
                <p className="text-xs text-white/50 mt-2">Add {money(50000 - subtotal)} more for free shipping.</p>
              )}
            </div>

            <div className="mt-6 space-y-3">
              <input
                type="text"
                placeholder="Full name"
                value={shipping.name}
                onChange={(e) => setShipping({ ...shipping, name: e.target.value })}
                className="w-full bg-black/40 border border-white/10 p-3 rounded"
              />
              <input
                type="text"
                placeholder="Phone"
                value={shipping.phone}
                onChange={(e) => setShipping({ ...shipping, phone: e.target.value })}
                className="w-full bg-black/40 border border-white/10 p-3 rounded"
              />
              <input
                type="text"
                placeholder="Address"
                value={shipping.address}
                onChange={(e) => setShipping({ ...shipping, address: e.target.value })}
                list="address-suggestions"
                className="w-full bg-black/40 border border-white/10 p-3 rounded"
              />
              <datalist id="address-suggestions">
                <option value="House number and street name" />
                <option value="Landmark + street name" />
                <option value="Apartment / floor / gate code" />
                <option value="Area, district, and nearest junction" />
              </datalist>
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={shipping.region}
                  onChange={(e) => setShipping({ ...shipping, region: e.target.value, city: '' })}
                  className="w-full bg-black/40 border border-white/10 p-3 rounded text-white"
                >
                  <option value="">Select region</option>
                  {regions.map((region) => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
                <select
                  value={shipping.city}
                  onChange={(e) => setShipping({ ...shipping, city: e.target.value })}
                  className="w-full bg-black/40 border border-white/10 p-3 rounded text-white"
                >
                  <option value="">Select city</option>
                  {availableCities.map((city) => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-white/50">Choose region first, then city. Include a clear street/landmark in address.</p>
              <textarea
                placeholder="Notes (optional)"
                value={shipping.notes}
                onChange={(e) => setShipping({ ...shipping, notes: e.target.value })}
                className="w-full bg-black/40 border border-white/10 p-3 rounded min-h-[80px]"
              />
              <input
                type="email"
                placeholder="Email for receipt"
                value={checkoutEmail}
                onChange={(e) => setCheckoutEmail(e.target.value)}
                className="w-full bg-black/40 border border-white/10 p-3 rounded"
              />
              <button
                onClick={startCheckout}
                disabled={checkoutLoading || !isCheckoutReady}
                className="w-full bg-[#A1FF4F] text-black font-bold py-3 rounded disabled:opacity-50"
              >
                {checkoutLoading ? 'Redirecting...' : 'Checkout with Paystack'}
              </button>
              {checkoutError && <p className="text-sm text-red-300">{checkoutError}</p>}
              {checkoutMessage && <p className="text-sm text-white/70">{checkoutMessage}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Footer Band */}
      <Footer />
    </div>
  );
}
